import { Table } from '@/components/custom/organisms/Table';
import { Button } from '@/components/ui/button';
import { useEffect, useMemo, useState } from 'react';
// import { ScrollArea } from "@components/ui/scroll-area";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { cloneDeep } from 'lodash';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import PageWrapper from '@/components/atoms/PageWrapper';
import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EditQuestionModal } from '@/features/question/components/organisms/EditQuestionModal';
import { AlertCircle, CheckCircle } from "lucide-react";
// import AddQuestionModal from '@/shared/components/AddQuestionModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// import { useGetOrgById } from '@/lib/useQuery/organisation/useOrganisation';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';

import { XlsxFileInput } from "@/components/atoms/XLSXInput";
import { SearchInput } from '@/components/molecules/SearchInput';
import { useGetOrganisationCrmList } from '@/features/crm/hooks/useQuery/useGetOrganisationCrmList';
import { downloadQuestionsTemplate } from '@/lib/supabase/queries/question';
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { saveAs } from 'file-saver';
import { useAddBankQuestions } from '@/lib/useQuery/question/useQuestions';

interface QuestionsFileStructure {
    "Question": string,
    "Question Identifier"?: string | undefined,
    "Additional Question Identifier"?: string | undefined,
    "Description"?: string | undefined
    "Field Type": string,
    "Field Min"?: number | undefined,
    "Field Max"?: number | undefined,
    "Field Options"?: string | undefined,
    "Field Integer Places"?: number | undefined,
    "Field Decimal Places"?: number | undefined,
    "Field Step"?: number | undefined
}

interface QuestionsTransformedFileStructure {
    id: string,
    question: string,
    crm_field_name?: string,
    crm_field_name_2?: string,
    description?: string,
    field_type: string,
    field_min?: number,
    field_max?: number,
    field_options?: string[] | undefined,
    field_integer_places?: number,
    field_decimal_places?: number,
    field_step?: number
}

export default function ImportQuestions() {
    const [searchParams, setSearchParams] = useSearchParams();
    const sp_crmId = searchParams.get("crmId");

    const { organization } = useOrganizationStore();
    const navigate = useNavigate();

    const [selectedCrmId, setSelectedCrmId] = useState<string | undefined>();
    const [searchPhrase, setSearchPhrase] = useState<string>();
    const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
    const [selectedQuestionData, setSelectedQuestionData] = useState<QuestionsTransformedFileStructure | undefined>();
    const [fileData, setFileData] = useState<QuestionsTransformedFileStructure[] | null>(null);

    const { data: orgCrmData, isLoading: isLoadingOrgCrmsByOrgId } = useGetOrganisationCrmList(Number(organization?.id))


    const { mutateAsync: addBankQuestions, isPending: isAddingBankQuestions } = useAddBankQuestions();

    const selectedCrmOptions = useMemo(() => {
        if (!orgCrmData) return [];
        const crmOptions = orgCrmData?.find((crm) => crm?.crm?.id === Number(selectedCrmId));
        if (!crmOptions || !Array.isArray(crmOptions?.crm_field_name_2_options)) return {};

        return crmOptions?.crm_field_name_2_options?.reduce((acc: Record<string, string>, option: { value: string, key: string }) => {
            if (!acc) return {};

            acc[option.value] = option.key;
            return acc;
        }, {});

    }, [orgCrmData, selectedCrmId]);


    const fieldNameToType = (fieldName: string) => {
        if (!fieldName) return null;
        return fieldName.split(' ').map(word => word.charAt(0).toLowerCase() + word.slice(1)).join('_');
    }

    const fieldTypeToName = (fieldType: string) => {
        if (!fieldType) return null;
        return fieldType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    const handleFileLoad = (data: unknown[]) => {
        const fileData = cloneDeep(data as QuestionsFileStructure[]);

        // console.log("[ImportQuestions] fileData: ", fileData);

        const transformedData = fileData.map((item) => {
            return {
                id: crypto.randomUUID(), // temporary id - will be replaced by server id
                question: item["Question"],
                description: item["Description"],
                crm_field_name: item["Question Identifier"],
                crm_field_name_2: item["Additional Question Identifier"],
                field_type: fieldNameToType(item["Field Type"]),
                field_min: item["Field Min"],
                field_max: item["Field Max"],
                field_options: item["Field Options"]?.split(';')?.map((opt) => opt?.trim() || '-'),
                field_integer_places: item["Field Integer Places"],
                field_decimal_places: item["Field Decimal Places"],
                field_step: item["Field Step"]
            }
        });

        setFileData(transformedData);
    };

    useEffect(() => {
        if (orgCrmData?.length) {
            const doesExists = orgCrmData?.some((crm) => String(crm?.crm?.id) === sp_crmId);
            if (doesExists && sp_crmId) {
                setSelectedCrmId(sp_crmId);
            } else {
                if (orgCrmData?.data?.[0]?.crm?.id) {
                    setSelectedCrmId(String(orgCrmData?.[0].crm.id));
                }
            }
        }
    }, [orgCrmData])

    const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        // TODO: debounce
        setSearchPhrase(e.target.value);
    }

    const errorsData = useMemo(() => {
        if (!fileData) return null;

        const checkErrors = (questionData: QuestionsTransformedFileStructure) => {
            const errors: { key: string, error: string[] }[] = [];

            if (!questionData.question) errors.push({ key: 'question', error: ['Name is required'] });
            if (!questionData.field_type) errors.push({ key: 'field_type', error: ['Field Type is required'] });
            // TODO: Add validation for types
            if (questionData.field_type && !['text', 'textarea', 'number', 'currency', 'radio', 'checkbox', 'picklist', 'picklist_multiple', 'slider'].includes(questionData.field_type.toLocaleLowerCase())) {
                errors.push({ key: 'field_type', error: ['Invalid Field Type'] });
            }
            if (questionData.field_type && ['text', 'textarea', 'number', 'currency'].includes(questionData.field_type.toLocaleLowerCase())) {
                if (!questionData.field_min) errors.push({ key: 'configuration', error: ['Min is required'] });
                if (!questionData.field_max) errors.push({ key: 'configuration', error: ['Max is required'] });
            }
            // if (questionData.field_type && ['number', 'currency'].includes(questionData.field_type.toLocaleLowerCase())) {
            //     if (!questionData.field_integer_places) errors.push({key: 'configuration', error: ['Integer Places is required']});
            //     if (!questionData.field_decimal_places) errors.push({key: 'configuration', error: ['Decimal Places is required']});
            // }
            if (questionData.field_type && ['radio', 'checkbox', 'picklist', 'picklist_multiple'].includes(questionData.field_type.toLocaleLowerCase())) {
                if (!questionData.field_options) errors.push({ key: 'configuration', error: ['Options are required'] });
            }
            if (questionData.field_type && ['slider'].includes(questionData.field_type.toLocaleLowerCase())) {
                if (!questionData.field_min) errors.push({ key: 'configuration', error: ['Min is required'] });
                if (!questionData.field_max) errors.push({ key: 'configuration', error: ['Max is required'] });
                if (!questionData.field_step) errors.push({ key: 'configuration', error: ['Step is required'] });
            }

            if (questionData.field_min !== undefined && isNaN(Number(questionData.field_min))) {
                errors.push({ key: 'configuration', error: ['Min must be a number'] });
            }
            if (questionData.field_max !== undefined && isNaN(Number(questionData.field_max))) {
                errors.push({ key: 'configuration', error: ['Max must be a number'] });
            }




            return errors;
        }

        return fileData.reduce((acc, item) => {
            const errors = checkErrors(item);
            if (errors.length) {
                acc[item.id] = errors;
            }
            return acc;
        }, {} as Record<string, { key: string, error: string[] }[]>);
    }, [fileData]);

    const tableBodyData = useMemo(() => {
        if (!fileData) return null;

        const isErrorInCell = (id: string, key: string) => {
            if (!errorsData) return false;
            return errorsData[id]?.some((error) => error.key === key) ? 'bg-red-300  h-full' : '';
        }

        const getOptionsBasedOnType = (questionData: QuestionsTransformedFileStructure) => {
            if (!questionData.field_type) return null;

            switch (questionData.field_type.toLocaleLowerCase()) {
                case 'text':
                case 'textarea':
                    return (
                        <div className={`p-1 ${isErrorInCell(questionData.id, 'configuration')}`}>
                            <div>Min: {questionData.field_min || '-'}</div>
                            <div>Max: {questionData.field_max || '-'}</div>
                        </div>
                    )
                case 'number':
                case 'currency':
                    return (
                        <div className={`p-1 ${isErrorInCell(questionData.id, 'configuration')}`}>
                            <div>Min: {questionData.field_min || '-'}</div>
                            <div>Max: {questionData.field_max || '-'}</div>
                            <div>Integer Places: {questionData.field_integer_places || '-'}</div>
                            <div>Decimal Places: {questionData.field_decimal_places || '-'}</div>
                        </div>
                    )
                case 'radio':
                case 'checkbox':
                case 'picklist':
                case 'picklist_multiple':
                    return (
                        <div className={`p-1 ${isErrorInCell(questionData.id, 'configuration')}`}>
                            <div>
                                <div>Options: </div>
                                <ul className="list-disc ms-4">
                                    {questionData.field_options?.map((opt) => <li key={opt}>{opt}</li>) || '-'}
                                </ul>
                            </div>
                        </div>
                    )
                case 'slider':
                    return (
                        <div className={`p-1 ${isErrorInCell(questionData.id, 'configuration')}`}>
                            <div>Min: {questionData.field_min || '-'}</div>
                            <div>Max: {questionData.field_max || '-'}</div>
                            <div>Step: {questionData.field_step || '-'}</div>
                        </div>
                    )
                default:
                    return null;
            }
        }

        return fileData.map((item) => {
            let crm_field_name_2 = item?.crm_field_name_2;
            if (crm_field_name_2) {
                crm_field_name_2 = selectedCrmOptions?.[item?.crm_field_name_2] || item?.crm_field_name_2;
            }

            return {
                id: item.id,
                errors: (
                    <div className={`h-full flex justify-center items-center p-1 text-center text-white ${errorsData?.[item.id] ? 'bg-red-500' : 'bg-green-500'} `}>
                        <TooltipProvider>
                            <div className="flex justify-center w-full gap-1">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {errorsData?.[item.id] ? (
                                            // <i className="text-xl text-red-500 bi bi-exclamation-circle"></i>
                                            <AlertCircle />
                                        ) : (
                                            // <i className="text-xl text-green-500 bi bi-check2"></i>
                                            <CheckCircle />
                                        )}
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <div className="text-left">
                                            {errorsData?.[item.id]?.map((error, index) => (
                                                <div key={index}>
                                                    {error.error.map((message, index) => (
                                                        <span key={index} className="">{message}</span>
                                                    ))}
                                                </div>
                                            )) || "No errors"}

                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </TooltipProvider>
                    </div>
                ),
                question: item.question,
                crm_field_name_2,
                description: (<div className={`p-1 text-center`}>{item.description}</div>),
                crm_field_name: (<div className={`p-1 text-center`}>{item.crm_field_name}</div>),
                field_type: (<div className={`p-1 text-center ${isErrorInCell(item.id, 'field_type')}`}>{fieldTypeToName(item.field_type)}</div>),
                configuration: getOptionsBasedOnType(item),
            }
        }).filter((item) => {
            if (!searchPhrase) return true;
            return item?.question?.toLowerCase().includes(searchPhrase.toLowerCase());
        }).map((item) => {
            return {
                ...item,
                question: (<div className={`p-1 text-center ${isErrorInCell(item.id, 'question')}`}>{item.question}</div>),
            }
        });
    }, [fileData, searchPhrase, errorsData, selectedCrmOptions]);

    const deleteHandler = async (id: string) => {
        const userDecision = await DecisionModal({ message: 'Once deleted, you will not be able to recover this question!' });
        if (!userDecision.isConfirmed) return;

        setFileData((prevData) => {
            return prevData?.filter((item) => item.id !== id) || null;
        });
    }

    const openEditHandler = (id: string) => {
        const selectedQuestion = fileData?.find((item) => item.id === id) || undefined;
        setSelectedQuestionData(selectedQuestion);
        setShowAddQuestionModal(true);
    }

    const closeEditHandler = () => {
        setSelectedQuestionData(undefined);
        setShowAddQuestionModal(false);
    }

    const onEditHandler = (data) => {
        setFileData((prevData) => {
            return prevData?.map((item) => {
                return item.id === data.id ? { ...item, ...(data.question) } : item
            }) || null;
        });
    }

    const onSubmitImportQuestions = async () => {
        if (!fileData) return;

        const crm_system = orgCrmData?.find((crm) => String(crm?.crm?.id) === String(selectedCrmId))?.crm?.crm_system;

        const requestBody = fileData?.map((item) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...rest } = item;
            return {
                ...rest,
                organisation_id: Number(organization?.id),
                field_placeholder: undefined,
                field_date_options: undefined,
                crm_id: Number(selectedCrmId),
                crm_system
            };
        });

        const result = await addBankQuestions(requestBody);
        if (result.status === 201) {
            SuccessPopup('Questions imported successfully!');
            navigate(`../questions-bank?crmId=${selectedCrmId}`);
        } else {
            ErrorPopup('Failed to import questions!');
        }
    }

    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const downloadTemplateHandler = async () => {
        const isCrmField2Enabled = orgCrmData?.find((crm) => crm?.crm?.id === Number(selectedCrmId))?.crm?.use_crm_field_name_2;

        setIsDownloadingTemplate(true);
        const result = await downloadQuestionsTemplate(`questions/question-template-${isCrmField2Enabled ? "2" : "1"}.xlsx`);
        setIsDownloadingTemplate(false);

        if (result.data) {
            saveAs(result.data, 'questions-template.xlsx');
            SuccessPopup('Template downloaded successfully! Check your downloads folder.');
        } else {
            ErrorPopup('Failed to download template!');
        }
    }

    const isEmptyObject = (obj: object | null): boolean => {
        if (!obj) return true;
        return Object.keys(obj).length === 0;
    };

    const tableColumns = useMemo(() => {
        const crmData = orgCrmData?.find((crm) => crm?.crm?.id === Number(selectedCrmId));

        const headCols = [
            {
                label: "",
                id: "errors",
                icon: "bi bi-info-circle",
                isSort: false
            },
            {
                label: "Name",
                id: "question",
                icon: "bi bi-alphabet",
                isSort: true
            },
            {
                label: "Description",
                id: "description",
                icon: "bi bi-text-left",
                isSort: true
            },
            {
                label: "Question Identifier",
                id: "crm_field_name",
                icon: "bi bi-fingerprint",
                isSort: true
            },
            {
                label: "Type",
                id: "field_type",
                icon: "bi bi-menu-app",
                isSort: true
            },
            {
                label: "Configuration",
                id: "configuration",
                icon: "bi bi-wrench-adjustable-circle",
                isSort: true
            },
            {
                label: "Actions",
                id: "actions",
                icon: "bi bi-gear-fill",
                isSort: false
            }
        ];

        const bodyCols = [
            {
                colId: "errors",
                dataId: "errors"
            },
            {
                colId: "question",
                dataId: 'question'
            },
            {
                colId: "description",
                dataId: "description"
            },
            {
                colId: "crm_field_name",
                dataId: "crm_field_name"
            },
            {
                colId: "field_type",
                dataId: "field_type"
            },
            {
                colId: "configuration",
                dataId: "configuration"
            },
            {
                colId: "actions",
                dataId: "actions"
            }
        ];

        if (crmData?.crm?.use_crm_field_name_2 === true) {
            const index = 4;

            const newHeadCols = [
                ...headCols.slice(0, index),
                {
                    label: crmData?.crm?.crm_field_name_2_name ?? "Additional Question Identifier",
                    id: "crm_field_name_2",
                    icon: "bi bi-fingerprint",
                    isSort: true
                },
                ...headCols.slice(index)
            ];

            const newBodyCols = [
                ...bodyCols.slice(0, index),
                {
                    colId: "crm_field_name_2",
                    dataId: "crm_field_name_2"
                },
                ...bodyCols.slice(index)
            ];

            return {
                headCols: newHeadCols,
                bodyCols: newBodyCols
            }
        }

        return {
            headCols,
            bodyCols
        }
    }, [selectedCrmId, orgCrmData]);

    const onChangeSelectedCrmId = (value: string) => {
        setSelectedCrmId(value);
        setSearchParams({ crmId: value });
    };

    return (
        <>
            {(isAddingBankQuestions || isDownloadingTemplate || isLoadingOrgCrmsByOrgId) && (<LoaderWithBackdrop />)}

            <EditQuestionModal
                show={showAddQuestionModal}
                onHide={closeEditHandler}
                onSubmit={onEditHandler}

                questionData={selectedQuestionData}

                crmId={selectedCrmId ? Number(selectedCrmId) : undefined}
                editInParentComponent={true}
            />

            <PageWrapper>
                <div className="flex flex-col px-1 m-0 overflow-hidden grow flex-nowrap" style={{ minHeight: 'calc(100vh - 98px)' }}>
                    <NavigationBarBreadCrumbs.Set
                        currentPageName="Import Questions"
                        pages={[
                            {
                                title: "Manage Organizations",
                                goTo: "/manage-organizations"
                            },
                            {
                                title: organization?.name || "Organization (?)",
                                goTo: `/organization?orgId=${organization?.id}`
                            },
                            {
                                title: "Manage Questions",
                                goTo: `../questions-bank`
                            },
                        ]}
                    />
                    {/* <BackNav
                        currentPageName="Import Questions"
                        pages={[
                            {
                                title: "Manage Organizations",
                                goTo: "/manage-organizations"
                            },
                            {
                                title: organization?.name || "Organization (?)",
                                goTo: `/organization?orgId=${organization?.id}`
                            },
                            {
                                title: "Manage Questions",
                                goTo: `../questions-bank`
                            },
                        ]}
                    /> */}

                    <div className="flex gap-3 mb-3">
                        <div className="grow">
                            <p className="mb-1 text-sm">
                                Click to upload
                            </p>
                            <XlsxFileInput onFileLoad={handleFileLoad} />
                        </div>

                        <div className="flex items-end">
                            <Button variant="accent" onClick={downloadTemplateHandler}>
                                <i className="bi bi-download me-2"></i>Download Template
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col grow">
                        <p className="mb-1 font-medium">
                            Check data and edit if needed
                        </p>
                        <hr className="mb-3" />

                        {/* <div className="flex justify-end mb-3 space-x-2">
                            <SearchInput 
                                setPhrase={setSearchPhrase}
                                searchedPhraseChangeHandler={() => {}}
                            />
                        </div> */}

                        <div className="flex flex-wrap items-center justify-between mb-3">
                            <div className="relative w-full sm:w-1/2 md:w-1/3 sm:pe-2">
                                <Select onValueChange={onChangeSelectedCrmId} value={selectedCrmId}>
                                    <SelectTrigger className="bg-gray-100 border">
                                        <SelectValue placeholder="Select crm type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {orgCrmData?.map((option: { crm: { id: number, name: string, crm_system: string } }) => (
                                            <SelectItem key={option.crm.id} value={String(option.crm.id)}>
                                                {option.crm.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <SearchInput
                                setValue={setSearchPhrase}
                                searchedPhraseChangeHandler={() => { }}
                            />
                        </div>


                        <div className="flex flex-col overflow-y-scrollwissp-scroll-box pe-2" style={{ height: "calc(100vh - 360px)" }}>
                            <Table
                                applyDefaultCellStyles={false}
                                headCols={tableColumns.headCols}
                                bodyCols={tableColumns.bodyCols}

                                actionsColumn={(id) => (
                                    <>
                                        <div className="p-0">
                                            <TooltipProvider>
                                                <div className="flex justify-center w-full gap-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                className='flex text-sm text-nowrap'
                                                                onClick={() => openEditHandler(id)}
                                                                size='action'
                                                            >
                                                                <i className="text-md bi bi-pencil-fill"></i>
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Edit</p>
                                                        </TooltipContent>
                                                    </Tooltip>

                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="destructive"
                                                                size='action'
                                                                className={`text-nowrap flex text-sm`}
                                                                onClick={() => deleteHandler(id)}
                                                            >
                                                                <i className="text-md bi bi-trash"></i>
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            Delete
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </TooltipProvider>
                                        </div>
                                    </>
                                )}

                                bodyData={tableBodyData}
                            />
                        </div>
                    </div>

                    <div className="w-full mt-3">
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Link to={`../questions-bank?crmId=${selectedCrmId}`}>
                                <Button variant="secondary" type="button">Back</Button>
                            </Link>
                            <Button disabled={!fileData || !isEmptyObject(errorsData)} type="button" onClick={onSubmitImportQuestions}>Submit</Button>
                        </div>
                    </div>
                </div>
            </PageWrapper>
        </>
    )
}
